import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { noop } from 'lodash';

import './filterableUnitList.scss';

import { UnitListV2, BookableUnitDetailsV2 } from 'components/unit/UnitList';
import { UnitSizeGuideV2 } from 'components/unit/UnitSizeGuideV2';

import { Button } from 'components/core/Button';
import { Popup } from 'components/core/Popup';
import { ModalLink } from 'components/core/ModalLink';
import { Modal, ModalToolbar, ModalBody } from 'components/core/ModalV2';
import { Text } from 'components/core/Text';
import { H3, H1, H2 } from 'components/core/Typography';
import { Image } from 'components/core/Image';
import { Checkbox } from 'components/core/Checkbox';
import SearchProperty from 'assets/images/psp/search-property.svg';
import filtersIcon from 'assets/images/icons/filters.svg';
import { ChevronRight } from 'components/svg';

// Size category configuration based on screenshot (Mini/Small/Medium/Large)
// Ranges expressed in square footage buckets (inclusive bounds)
export const SIZE_CATEGORIES = [
	{ key: 'mini', label: 'Mini', range: [0, 25], displayRange: '0-25 sq ft' },
	{
		key: 'small',
		label: 'Small',
		range: [26, 75],
		displayRange: '26-75 sq ft',
	},
	{
		key: 'medium',
		label: 'Medium',
		range: [76, 200],
		displayRange: '76-200 sq ft',
	},
	{
		key: 'large',
		label: 'Large',
		range: [201, Infinity],
		displayRange: '201+ sq ft',
	},
];

// Helper to derive sqft numeric average or parse bucket strings like '20-37'
export function parseSqft(unit) {
	// Support either unit.sqft numeric, unit.sqftBucket string, or sizeBucket mapping
	const sqftVal = unit?.sqft || unit?.sqftBucket || unit?.squareFootage;
	if (typeof sqftVal === 'number') return sqftVal;
	if (typeof sqftVal === 'string') {
		// pattern "A-B" or single number
		const parts = sqftVal.split('-');
		if (parts.length === 2) {
			const [a, b] = parts.map((p) => parseFloat(p));
			if (!isNaN(a) && !isNaN(b)) return (a + b) / 2; // midpoint
		}
		const single = parseFloat(parts[0]);
		if (!isNaN(single)) return single;
	}
	return 0; // fallback
}

 export function categorizeUnit(unit) {
	const sqft = parseSqft(unit);
	return SIZE_CATEGORIES.find(
		({ range }) => sqft >= range[0] && sqft <= range[1]
	)?.key;
}

export function FilterableUnitList({
	units = [],
	facility,
	phone,
	className,
	unitDetailsComponent: UnitDetailsComponent = BookableUnitDetailsV2,
	unitDetailsComponentProps = noop,
	scrollToStorageCalculator,
}) {
	// Keep only selected category keys
	const [activeCategories, setActiveCategories] = useState(() =>
		SIZE_CATEGORIES.map((c) => c.key)
	);
	const [showFilters, setShowFilters] = useState(false);
	const [showSizeGuideModal, setShowSizeGuideModal] = useState(false);

	// Map units to categories once
	const unitsWithCategory = useMemo(
		() => units.map((u) => ({ ...u, _sizeCategory: categorizeUnit(u) })),
		[units]
	);

	// Derived filtered units
	const filteredUnits = useMemo(() => {
		if (!activeCategories.length) return [];
		return unitsWithCategory.filter((u) =>
			activeCategories.includes(u._sizeCategory)
		);
	}, [unitsWithCategory, activeCategories]);

	// Category metadata with disabled state
	const categoryMeta = useMemo(
		() =>
			SIZE_CATEGORIES.map((c) => ({
				...c,
				count: unitsWithCategory.filter(
					(u) => u._sizeCategory === c.key
				).length,
			})),
		[unitsWithCategory]
	);

	const allSelected = activeCategories.length === SIZE_CATEGORIES.length;
	const noneSelected = activeCategories.length === 0;

	const toggleCategory = useCallback((key) => {
		setActiveCategories((prev) =>
			prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
		);
	}, []);

	const handleSelectAll = useCallback(
		() => setActiveCategories(SIZE_CATEGORIES.map((c) => c.key)),
		[]
	);
	const handleClearAll = useCallback(() => setActiveCategories([]), []);

	if (units.length === 0) {
		return null;
	}

	// Filters UI block (shared for sidebar and modal)
	const filtersBlock = (
		<div className="filters-container">
			<div className="filter-header">
				<div className="filter-actions-wrapper">
					<span>Filters</span>
					<div className="filter-actions">
						<button
							type="button"
							className={classnames('action-link', {
								disabled: noneSelected,
							})}
							onClick={handleClearAll}
							disabled={noneSelected}
						>
							Clear All
						</button>
						<button
							type="button"
							className={classnames('action-link', {
								disabled: allSelected,
							})}
							onClick={handleSelectAll}
							disabled={allSelected}
						>
							Select All
						</button>
					</div>
				</div>
			</div>
			<div className="size-groups">
				<p
					size="small"
					className="text-light"
				>
					SIZE
				</p>
				<ul className="size-category-list">
					{categoryMeta.map((cat, idx) => {
						const checked = activeCategories.includes(cat.key);
						return (
							<li
								key={cat.key}
								className="size-category-item"
							>
								<Checkbox
									id={`unit-size-filter-${idx}`}
									name={`unit-size-${cat.key}`}
									label={
										<span className="label-text">
											<Text className='label' >{cat.label}</Text>
											<Text
												className="range"
												size="small"
												color="gray"
											>
												{' '}
												{cat.displayRange}
											</Text>
										</span>
									}
									input={{
										value: checked,
										onChange: () => toggleCategory(cat.key),
									}}
								/>
							</li>
						);
					})}
				</ul>
			</div>
			<ModalLink
				className="storage-unit-size"
				text="Learn about storage unit sizes"
				size={12}
				hideIcon={true}
				onClick={() => {
					setShowSizeGuideModal(true);
					setShowFilters(false);
				}}
			/>
			{/* <div className='filters-border-top'>df</div> */}
			<div
				onClick={() => setShowFilters(false)}
				className="filters-border-top"
			>
				<Button
					className="apply-filters-button"
					pink
					fullWidth
				>
					Apply Filters
				</Button>
				<div
					className="space-calculator"
					onClick={scrollToStorageCalculator}
				>
					<Image
						src={SearchProperty}
						alt="Search Property"
					/>
					<span className="space-calculator-text">
						How much space do you need?
					</span>
					<ChevronRight />
				</div>
			</div>
		</div>
	);

	return (
		<>
			<div
				className={classnames('filterable-units-container', className)}
			>
				<div
					className={classnames('filters-modal-toggle', {
						'filters-open': showFilters,
					})}
				>
					<div className="available-units-header">
						<H2 className="title-row">Available Units </H2>
						<span className="available-units-count">
							{' '}
							({filteredUnits.length})
						</span>
					</div>
					<Popup
						buttonContent={
							<Button
								white
								onClick={() => setShowFilters(!showFilters)}
								className="filter-button-mobile"
							>
								{' '}
								<Image
									src={filtersIcon}
									alt="Filter Icon"
								/>{' '}
								Filters
							</Button>
						}
						position="bottom-right"
						open={showFilters}
						onToggle={setShowFilters}
					>
						{filtersBlock}
					</Popup>
				</div>
				<aside className="filters-sidebar">
					<div className="available-units-header">
						<H2 className="title-row">Available Units</H2>
						<span className="available-units-count">
							{' '}
							({filteredUnits.length})
						</span>
					</div>
					{filtersBlock}
				</aside>
				<UnitListV2
					units={filteredUnits}
					facility={facility}
					phone={phone}
					unitDetailsComponent={UnitDetailsComponent}
					unitDetailsComponentProps={unitDetailsComponentProps}
				/>
			</div>
			{showSizeGuideModal && (
				<Modal
					showing={showSizeGuideModal}
					onClose={() => setShowSizeGuideModal(false)}
					size="wide"
					closeable={true}
					closeButtonText="Close"
					toolbar={
						<ModalToolbar>
							<H3>Storage Unit Sizes</H3>
						</ModalToolbar>
					}
				>
					<ModalBody>
						<UnitSizeGuideV2 />
					</ModalBody>
				</Modal>
			)}
		</>
	);
}

FilterableUnitList.propTypes = {
	units: PropTypes.arrayOf(PropTypes.object),
	facility: PropTypes.object,
	phone: PropTypes.object,
	className: PropTypes.string,
	unitDetailsComponent: PropTypes.oneOfType([
		PropTypes.func,
		PropTypes.elementType,
	]),
	unitDetailsComponentProps: PropTypes.func,
	scrollToStorageCalculator: PropTypes.func.isRequired,
};

export default FilterableUnitList;
